import CollectionsManager from '../MovieCollections';
import YearInMovies from '../YearInMovies';
import HedoneWrapped from '../HedoneWrapped';
import PersonalPicks from '../PersonalPicks';
import { HideWatchedControl, useWatched } from '../DiscoveryPreferences';
import { PersonalCollections } from "../PeopleFeatures";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FiArrowUpRight,
  FiBookmark,
  FiCheck,
  FiHeart,
  FiLogOut,
  FiRefreshCw,
  FiSliders,
  FiStar,
} from "react-icons/fi";
import { useAuth } from "../AuthContext";
import { LikeButton, WatchlistButton } from "../LibraryActions";
import "./PersonalSpace.css";

const genreOptions = [
  [28, "Action"], [12, "Adventure"], [16, "Animation"], [35, "Comedy"],
  [80, "Crime"], [99, "Documentary"], [18, "Drama"], [14, "Fantasy"],
  [27, "Horror"], [9648, "Mystery"], [10749, "Romance"], [878, "Sci-Fi"],
  [53, "Thriller"],
];
const moodOptions = ["Comforting", "Adventurous", "Mind-bending", "Intense", "Romantic", "Funny"];
const PersonalSpace = () => {
  const {
    user,
    authReady,
    preferences,
    libraryItems,
    openAuth,
    logout,
    savePreferences,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {watched} = useWatched();
  useEffect(() => {
    if (!user || !authReady || !location.hash) return;
    const timer = setTimeout(() => document.getElementById(location.hash.slice(1))?.scrollIntoView({block:"start"}),100);
    return () => clearTimeout(timer);
  },[user,authReady,location.hash]);
  const [genreIds, setGenreIds] = useState([]);
  const [moods, setMoods] = useState([]);
  const [contentType, setContentType] = useState("both");
  const [profileMode, setProfileMode] = useState("general");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    setGenreIds(preferences.genreIds || []);
    setMoods(preferences.moods || []);
    setContentType(preferences.contentType || "both");
    setProfileMode(preferences.profileMode || "general");
  }, [preferences]);

  const watchlist = useMemo(
    () => libraryItems.filter((item) => item.watchlist),
    [libraryItems]
  );
  const liked = useMemo(
    () => libraryItems.filter((item) => item.liked),
    [libraryItems]
  );

  const toggleChoice = (value, values, setValues, limit = 6) => {
    setSaved(false);
    setValues(values.includes(value)
      ? values.filter((item) => item !== value)
      : [...values, value].slice(0, limit));
  };

  const saveTaste = async () => {
    setSaving(true); setSaveError('');
    try { if (await savePreferences({ genreIds, moods, contentType, profileMode })) setSaved(true); }
    catch { setSaveError('Could not save your taste. Please try again.'); }
    finally { setSaving(false); }
  };

  if (!authReady) return <main className="personal-space personal-space-loading"><span>Preparing your space…</span></main>;

  if (!user) {
    return (
      <main className="personal-space personal-space-gate">
        <div className="personal-gate-card">
          <span className="personal-orbit"><FiStar /></span>
          <p className="personal-kicker">YOUR PERSONAL SPACE</p>
          <h1>A watchlist that learns what you love.</h1>
          <p>Sign in with Google to keep movies and series across devices and unlock recommendations shaped by your taste.</p>
          <button onClick={openAuth}>Continue with Google <FiArrowUpRight /></button>
        </div>
      </main>
    );
  }

  const displayName = user.displayName?.split(" ")[0] || "Viewer";

  return (
    <main className="personal-space">
      <header className="personal-header">
        <div className="personal-identity">
          {user.photoURL ? <img src={user.photoURL} alt="" referrerPolicy="no-referrer" /> : <span>{displayName[0]}</span>}
          <div><p className="personal-kicker">YOUR HEDONE</p><h1>Welcome back, {displayName}.</h1><p>Your taste, saved titles, and next great watch—all in one place.</p></div>
        </div>
        <button className="personal-signout" onClick={logout}><FiLogOut /> Sign out</button>
      </header>

      <section className="personal-stats" aria-label="Your library summary">
        <div><FiBookmark /><span><strong>{watchlist.length}</strong>Watch later</span></div>
        <div><FiHeart /><span><strong>{liked.length}</strong>Liked titles</span></div>
        <div><FiStar /><span><strong>{watched.size}</strong>Movies watched</span></div>
      </section>

      <HideWatchedControl />
      <CollectionsManager />
      <YearInMovies />
      <HedoneWrapped />
      <PersonalCollections />
      <PersonalPicks />
      <section className="taste-panel">
        {saveError && <p role="alert">{saveError}</p>}
        <div className="taste-heading"><span><FiSliders /></span><div><p className="personal-kicker">TASTE PROFILE</p><h2>Tell Hedone what feels right.</h2><p>Choose up to six genres and three moods. You can change these anytime.</p></div></div>
        <div className="taste-grid">
          <div><label>Genres</label><div className="choice-cloud">{genreOptions.map(([id, label]) => <button key={id} className={genreIds.includes(id) ? "is-selected" : ""} onClick={() => toggleChoice(id, genreIds, setGenreIds, 6)}>{genreIds.includes(id) && <FiCheck />}{label}</button>)}</div></div>
          <div><label>Tonight I want something…</label><div className="choice-cloud">{moodOptions.map((mood) => <button key={mood} className={moods.includes(mood) ? "is-selected" : ""} onClick={() => toggleChoice(mood, moods, setMoods, 3)}>{moods.includes(mood) && <FiCheck />}{mood}</button>)}</div></div>
          <div><label>Show me</label><div className="choice-cloud compact">{[["both","Movies & series"],["movie","Movies"],["tv","Series"]].map(([id,label]) => <button key={id} className={contentType === id ? "is-selected" : ""} onClick={() => { setContentType(id); setSaved(false); }}>{label}</button>)}</div></div>
          <div><label>Viewing profile</label><div className="choice-cloud compact">{[["general","General"],["family","Family"],["kids","Kids"]].map(([id,label]) => <button key={id} className={profileMode === id ? "is-selected" : ""} onClick={() => { setProfileMode(id); setSaved(false); }}>{profileMode === id && <FiCheck/>}{label}</button>)}</div></div>
        </div>
        <button className="save-taste" onClick={saveTaste} disabled={saving}>{saved ? <><FiCheck /> Taste saved</> : saving ? "Saving…" : "Save my taste"}</button>
      </section>

      <LibrarySection title="Watch later" subtitle="Your private queue, available wherever you sign in." items={watchlist} empty="Tap + on any movie or series to build your queue." navigate={navigate} />
      <LibrarySection title="Titles you liked" subtitle="The strongest signal for making your recommendations better." items={liked} empty="Tap the heart on titles you love." navigate={navigate} />
    </main>
  );
};

const LibrarySection = ({ title, subtitle, items, empty, navigate, recommendations = false, loading = false }) => (
  <section className="personal-library">
    <header><div><h2>{title}</h2><p>{subtitle}</p></div>{loading && <FiRefreshCw className="recommendation-spinner" />}</header>
    {!loading && !items.length ? <div className="personal-empty">{empty}</div> : (
      <div className="personal-card-row">
        {items.map((item) => {
          const mediaType = item.mediaType || item.media_type;
          const titleText = item.title || item.name;
          const poster = item.posterPath || item.poster_path;
          return (
            <article className="personal-card" key={`${mediaType}_${item.id}`} onClick={() => navigate(`/${mediaType}/${item.id}`)}>
              <img src={`https://image.tmdb.org/t/p/w342${poster}`} alt={titleText} loading="lazy" />
              <div className="personal-card-shade" />
              <span className="personal-type">{mediaType === "movie" ? "MOVIE" : "SERIES"}</span>
              <div className="personal-card-copy">{recommendations && <small>{item.recommendationReason}</small>}<strong>{titleText}</strong><span>{(item.releaseDate || item.release_date || item.first_air_date || "").slice(0,4) || "TBA"}</span></div>
              <div className="personal-card-actions"><WatchlistButton item={item} mediaType={mediaType} compact /><LikeButton item={item} mediaType={mediaType} compact /></div>
            </article>
          );
        })}
      </div>
    )}
  </section>
);

export default PersonalSpace;
